// Storage signal — detecta quota disponível pra IndexedDB + Cache API.
//
// Crítico em low-end: vlow Tizen 2017 quota é ~30-80MB. Cachear muito
// derruba o app (SO da TV mata por OOM). Capabilities derivadas usam
// pra capar swImageMaxEntries, episodeWindow, snapshotKeepCount.
//
// Async: navigator.storage.estimate() retorna Promise. Bloqueante no
// boot — capabilities críticas dependem disso. ~10-50ms overhead total
// (DNS lookup-like). Aceitável.

export type StorageTier = 'vlow' | 'low' | 'mid' | 'high' | 'unknown'

export interface StorageSignal {
  tier: StorageTier
  /** Quota total (bytes) — IDB + Cache API combinados. */
  quotaBytes: number
  /** Bytes em uso no momento da medição. */
  usageBytes: number
}

const DEFAULT_SIGNAL: StorageSignal = {
  tier: 'unknown',
  quotaBytes: 0,
  usageBytes: 0,
}

export async function readStorageSignal(): Promise<StorageSignal> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return DEFAULT_SIGNAL
  }
  try {
    const est = await navigator.storage.estimate()
    const quotaBytes = est.quota ?? 0
    const usageBytes = est.usage ?? 0
    if (quotaBytes === 0) return DEFAULT_SIGNAL

    // Limiares calibrados em MB pra match com realidade TV:
    // - Tizen 2017 (1GB RAM): quota tipica ~50MB → vlow
    // - Tizen 2019/webOS 4 (2GB): ~150MB → low
    // - TV moderna: ~500MB → mid
    // - TV box / PC: 1GB+ → high
    const quotaMB = quotaBytes / 1024 / 1024
    let tier: StorageTier
    if (quotaMB < 100) tier = 'vlow'
    else if (quotaMB < 300) tier = 'low'
    else if (quotaMB < 1000) tier = 'mid'
    else tier = 'high'

    return { tier, quotaBytes, usageBytes }
  } catch {
    return DEFAULT_SIGNAL
  }
}
